import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Drawer.module.css';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

interface DrawerHeaderProps {
  title: string;
  onClose: () => void;
}

interface DrawerContentProps {
  children: React.ReactNode;
}

interface DrawerFooterProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

interface DrawerButtonProps {
  onClick?: (e: React.FormEvent | React.MouseEvent) => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  children: React.ReactNode;
}

const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose, children }) => {
  const [shouldRender, setShouldRender] = React.useState(false);
  const [isAnimating, setIsAnimating] = React.useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      document.body.style.overflow = 'hidden';
      // Double requestAnimationFrame to ensure DOM is fully ready
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      document.body.style.overflow = 'unset';
      // Delay unmounting to allow transition to complete
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  return createPortal(
    <>
      <div
        className={`${styles.overlay} ${isAnimating ? styles.visible : styles.hidden}`}
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Close drawer"
      />
      <div className={`${styles.drawer} ${isAnimating ? styles.visible : styles.hidden}`}>
        {children}
      </div>
    </>,
    document.body
  );
};

// Header Component
const DrawerHeader: React.FC<DrawerHeaderProps> = ({ title, onClose }) => (
  <div className={styles.header}>
    <h3 className={styles.title}>{title}</h3>
    <button
      className={styles.closeButton}
      onClick={onClose}
      aria-label="Close drawer"
    >
      ×
    </button>
  </div>
);

// Content Component
const DrawerContent: React.FC<DrawerContentProps> = ({ children }) => (
  <div className={styles.content}>{children}</div>
);

// Footer Component
const DrawerFooter: React.FC<DrawerFooterProps> = ({ children, fullWidth }) => (
  <div className={`${styles.footer} ${fullWidth ? styles.fullWidth : ''}`}>{children}</div>
);

// Button Components
const PrimaryButton: React.FC<DrawerButtonProps> = ({
  onClick,
  disabled,
  type = 'button',
  children
}) => (
  <button
    type={type}
    className={`${styles.button} ${styles.primaryButton}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const SecondaryButton: React.FC<DrawerButtonProps> = ({
  onClick,
  disabled,
  type = 'button',
  children
}) => (
  <button
    type={type}
    className={`${styles.button} ${styles.secondaryButton}`}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

// Compound Component Structure
type DrawerComponent = React.FC<DrawerProps> & {
  Header: React.FC<DrawerHeaderProps>;
  Content: React.FC<DrawerContentProps>;
  Footer: React.FC<DrawerFooterProps>;
  PrimaryButton: React.FC<DrawerButtonProps>;
  SecondaryButton: React.FC<DrawerButtonProps>;
};

const DrawerWithSubComponents = Drawer as DrawerComponent;
DrawerWithSubComponents.Header = DrawerHeader;
DrawerWithSubComponents.Content = DrawerContent;
DrawerWithSubComponents.Footer = DrawerFooter;
DrawerWithSubComponents.PrimaryButton = PrimaryButton;
DrawerWithSubComponents.SecondaryButton = SecondaryButton;

export { DrawerWithSubComponents as Drawer };